import { Formik } from "formik";
import * as yup from "yup";
import React, { Fragment, useState } from "react";
import MainCalculatorsList from "../MainCalculators";
import SharedHelmet from '../Helmet';
import RelatedLinks from "../RelatedLinks";
import { CalculatorsType } from "../ConstantsEN";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import imageUrl from "../../../images/Root.png";
import { Button, TextField } from "@mui/material";
const TITLE = "Square and Cubic Root Calculator";
const DESCRIPTION =
  "This free calculator computes roots for numbers, including common roots like square root or cubic root, and calculates the root for any given number.";
const KEYWORDS = "calculator, root, roots, free, square root, cubic root, root calculation, numbers";
const OG_URL = "https://do-calculate.com/calculator/en/roots";
const rootSchema = yup.object().shape({
  xRoot1: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "Number is too large")
    .min(0, "Make sure not to enter negative values"),
  xRoot2: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "Number is too large")
    .min(0, "Make sure not to enter negative values"),
  xRoot3: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(1000000000, "Number is too large")
    .min(0, "Make sure not to enter negative values"),
  nRoot: yup
    .number()
    .typeError("Make sure the value is a number")
    .required("Make sure to enter a value")
    .max(999, "Number is too large"),
});
const initialValuesRoots = {
  xRoot1: 0,
  xRoot2: 0,
  xRoot3: 0,
  nRoot: 0,
};
const RootCalculator = () => {
  const [squareRoot, setSquareRoot] = useState(null);
  const [cubicRoot, setCubicRoot] = useState(null);
  const [nthRoot, setNthRoot] = useState(null);
  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        OG_IMAGE={imageUrl}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="row">
            <div>
              <p>
                The roots calculator provides an easy-to-use interface for calculating roots for any number. It allows users to understand how to calculate roots, whether they are square roots, cubic roots, or any other type.
              </p>
              <h2 style={{ fontSize: "17px" }}>
                Roots are calculated as follows:
              </h2>
              <ul>
                <li>
                  The value under the root raised to the power of 1 divided by the root value:
                </li>
                <li>
                  Result = Value under the root^(1/Root value)
                  <br />y = x^(1/n)
                </li>
                <li>Note: There are no roots for negative values.</li>
              </ul>
              <b>
                You can also use the following calculator to find the root value:
              </b>
              <hr />
            </div>
          </div>
          <MobileUnderCalAds />
          <div className="row">
            <div className="col-sm-12 col-md-7 calculatorStyle">
              <div className="p-3 mb-2 bg-success text-white">
                <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
              </div>
              <hr />
              <Formik
                initialValues={initialValuesRoots}
                validationSchema={rootSchema}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  setFieldValue,
                  resetForm,
                  isValid,
                }) => (
                  <div dir="ltr">
                    <h3 style={{ fontSize: "16px" }} className="mb-3">Square Root Calculator</h3>
                    <div
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <TextField
                        label="xRoot"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.xRoot1}
                        name="xRoot1"
                        error={
                          Boolean(touched.xRoot1) &&
                          Boolean(errors.xRoot1)
                        }
                        helperText={touched.xRoot1 && errors.xRoot1}
                        sx={{
                          width: "160px",
                          ml: "2px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor:
                              "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <div style={{ fontSize: "25px" }}>√</div>
                      <Button
                        disabled={!isValid}
                        color="success"
                        sx={{
                          backgroundColor: "rgb(25,135,84)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                        onClick={() => {
                          setSquareRoot(Math.sqrt(values.xRoot1));
                        }}
                      >
                        Calculate
                      </Button>
                      <Button
                        disabled={!isValid}
                        sx={{
                          backgroundColor: "rgb(108,117,125)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                        onClick={() => {
                          setSquareRoot(null);
                          resetForm();
                        }}
                      >
                        Clear Result
                      </Button>
                    </div>
                    <br />
                    {squareRoot && squareRoot > 0 ? (
                      <Fragment>
                        <p className="p-3 mb-2 bg-success text-white">
                          {` Result: Square root of ${values.xRoot1} is: ${squareRoot}`}
                        </p>
                        <p className="p-3 mb-2 bg-info text-black">
                          {` Solution: ${values.xRoot1}^(1/2) = ${squareRoot}`}
                        </p>
                      </Fragment>
                    ) : (
                      ""
                    )}
                    <hr />
                    <h3 style={{ fontSize: "16px" }} className="mb-3">Cubic Root Calculator</h3>
                    <div
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <TextField
                        label="xRoot"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.xRoot2}
                        name="xRoot2"
                        error={
                          Boolean(touched.xRoot2) &&
                          Boolean(errors.xRoot2)
                        }
                        helperText={touched.xRoot2 && errors.xRoot2}
                        sx={{
                          width: "160px",
                          ml: "2px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor:
                              "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <div style={{ fontSize: "25px" }}>∛</div>
                      <Button
                        disabled={!isValid}
                        color="success"
                        sx={{
                          backgroundColor: "rgb(25,135,84)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                        onClick={() => {
                          setCubicRoot(Math.cbrt(values.xRoot2));
                        }}
                      >
                        Calculate
                      </Button>
                      <Button
                        disabled={!isValid}
                        sx={{
                          backgroundColor: "rgb(108,117,125)",
                          fontSize: "15px",
                          marginRight: "25px",
                          padding: "5px 25px",
                          color: "white",
                        }}
                        onClick={() => {
                          setCubicRoot(null);
                          resetForm();
                        }}
                      >
                        Clear Result
                      </Button>
                    </div>
                    <br />
                    {cubicRoot && cubicRoot > 0 ? (
                      <Fragment>
                        <p className="p-3 mb-2 bg-success text-white">
                          {` Result: Cubic root of ${values.xRoot2} is: ${cubicRoot}`}
                        </p>
                        <p className="p-3 mb-2 bg-info text-black">
                          {` Solution: ${values.xRoot2}^(1/3) = ${cubicRoot}`}
                        </p>
                      </Fragment>
                    ) : (
                      ""
                    )}
                    <hr />
                    <h3 style={{ fontSize: "16px" }} className="mb-3">Root Calculator for Any Desired Number</h3>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <TextField
                        label="xRoot"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.xRoot3}
                        name="xRoot3"
                        error={
                          Boolean(touched.xRoot3) &&
                          Boolean(errors.xRoot3)
                        }
                        helperText={touched.xRoot3 && errors.xRoot3}
                        sx={{
                          width: "100px",
                          ml: "2px",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor:
                              "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <div style={{ fontSize: "25px" }}>√</div>
                      <TextField
                        label="nRoot"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.nRoot}
                        name="nRoot"
                        error={
                          Boolean(touched.nRoot) && Boolean(errors.nRoot)
                        }
                        helperText={touched.nRoot && errors.nRoot}
                        sx={{
                          width: "60px",
                          ml: "2px",
                          mr: "2px",
                          transform: "translateY(-15px)",
                          bgcolor: "#f9f9f9",
                          "&  .MuiFormHelperText-root.Mui-error": {
                            backgroundColor:
                              "rgb(229, 230, 227) !important",
                            margin: 0,
                            padding: "8px 0 0 41px",
                          },
                        }}
                        size="small"
                        color="success"
                      />
                      <div className="my-3">
                        <Button
                          disabled={!isValid}
                          color="success"
                          sx={{
                            backgroundColor: "rgb(25,135,84)",
                            fontSize: "15px",
                            marginRight: "25px",
                            padding: "5px 25px",
                            color: "white",
                          }}
                          onClick={() => {
                            setNthRoot(
                              Math.pow(values.xRoot3, 1 / values.nRoot)
                            );
                          }}
                        >
                          Calculate
                        </Button>
                        <Button
                          disabled={!isValid}
                          sx={{
                            backgroundColor: "rgb(108,117,125)",
                            fontSize: "15px",
                            marginRight: "25px",
                            padding: "5px 25px",
                            color: "white",
                          }}
                          onClick={() => {
                            setNthRoot(null);
                            resetForm();
                          }}
                        >
                          Clear Result
                        </Button>
                      </div>
                    </div>
                    {nthRoot && nthRoot > 0 && nthRoot !== Infinity ? (
                      <Fragment>
                        <p className="p-3 mb-2 bg-success text-white">
                          {` Result: ${values.nRoot}-th root of ${values.xRoot3} is: ${nthRoot}`}
                        </p>
                        <p className="p-3 mb-2 bg-info text-black">
                          {` Solution: ${values.xRoot3}^(1/${values.nRoot}) = ${nthRoot}`}
                        </p>
                      </Fragment>
                    ) : (
                      ""
                    )}
                    <br />
                  </div>
                )}
              </Formik>
            </div>
          </div>
          <hr />
          <div className="row">
            <RelatedLinks />
            <hr />
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList
            CalculatorsTypeId={CalculatorsType.mathCalculators}
          ></MainCalculatorsList>
          <br />
        </div>
      </div>
      <div className="row" style={{ height: 'auto' }} >
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <u><h3 style={{ fontSize: '16px' }}>General Root Calculator - Calculating Square and Cubic Roots</h3></u>
              </div>
              <div className="info-section-body">
                <p>
                  In mathematics, the general root of an integer (s) is defined by another number represented by the letter (b), such that the result of repeatedly multiplying this number by itself n times equals (s). This is expressed in the equation:
                </p>
                <p>
                  <math display="block">
                    <msup>
                      <mroot>
                        <mrow>
                          <mi>a</mi>
                        </mrow>
                        <mn>n</mn>
                      </mroot>
                      <mo>=</mo>
                      <mi>b</mi>
                    </msup>
                  </math>
                </p>
                <p>
                  <math display="block">
                    <msup>
                      <mi>b</mi>
                      <mn>n</mn>
                    </msup>
                    <mo>=</mo>
                    <mi>a</mi>
                  </math>
                </p>
                <h4>Root Estimation</h4>
                <p>
                  Some common roots include the square root (n=2) and the cubic root (n=3). Calculating square and cubic roots requires estimation and trial.
                </p>
                <p>
                  To calculate √s:
                </p>
                <ol>
                  <li>Estimate a number named (b).</li>
                  <li>Divide (s) by (b). If the resulting number (c) is accurate to the desired decimal place, stop.</li>
                  <li>Calculate the average between (b) and (c) and use the result as a new estimate.</li>
                  <li>Repeat step two.</li>
                </ol>
                <h4>Example:</h4>
                <p>
                  Finding <strong>³√125</strong> approximated to 3 decimal places:
                </p>
                <p>
                  <strong>Estimation: 5.432</strong>
                  <br />
                  125 ÷ 5.432³ = 5.452
                  <br />
                  (5.432 × 3 + 5.452) / 4 = 5.447
                  <br />
                  125 ÷ 5.447³ = 5.445
                  <br />
                  (5.445 × 3 + 5.447) / 4 = 5.446
                </p>
                <p>
                  It can be inferred from this that continuing the calculation will lead to a number approximating 5.446, making it the final estimation to 3 decimal places.
                </p>
                <h4>Square Root</h4>
                <p>
                  The square root in mathematics is the positive real number (r) that, when multiplied by itself, equals the number (s). For example, the square root of 16 is the number 4, as 4 multiplied by itself gives the result 16. In mathematics, the symbol √ is used to represent the square root, and the expression can be read as "the square root of 16 is equal to 4."
                </p>
                <h4>Example:</h4>
                <p>
                  Let's calculate the square root of 49. Using the square root symbol √, the calculation is as follows:
                </p>
                <code>
                  √49 = 7
                </code>
                <p>
                  Here, the square root of 49 is 7, as 7 multiplied by itself equals 49.
                </p>
                <h4>Cubic Root</h4>
                <p>
                  The cubic root is defined as the number that, when multiplied by itself twice, equals the number (s). The cubic root is represented using the symbol <strong>³√</strong>, and it denotes the third root of the number. An example of the cubic root is <strong>³√27 = 3</strong>; where 3 raised to the cubic power twice equals 27.
                </p>
                <p>
                  <strong>Square and Cubic Root Calculator Program</strong>
                </p>
                <p>
                  Many may not know how to calculate square and cubic roots using a calculator for any number, or they may lack a calculator with root functionality. Fortunately, we have developed a simple program that performs these operations in less than a second. All you need to do is enter the number in the designated field and then click the "Calculate" button to obtain the square or cubic root value, displayed on the screen.
                </p>
                <p>
                  <strong>Note:</strong> Examples and details can be modified to fit the context of the application or website.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default RootCalculator;
